import { useState, useContext, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CartContext } from '@/context/CartContext';
import CartDrawer from './CartDrawer';

export default function Navbar() {
  const { itemCount, openDrawer } = useContext(CartContext);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartBounce, setCartBounce] = useState(false);
  const prevCount = useRef(itemCount);

  useEffect(() => {
    if (itemCount > prevCount.current) {
      setCartBounce(true);
      setTimeout(() => setCartBounce(false), 400);
    }
    prevCount.current = itemCount;
  }, [itemCount]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-primary-500 shadow-sm'
            : 'bg-primary-500'
        }`}
      >
        <div className="max-w-none mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <img
                src="https://storage.readdy-site.link/project_files/4ae51fa3-40da-40a1-b133-2a5cd9cb4858/eeeb8ac8-0d7f-481e-9e59-4803245bf0d2_compressed_joviaa-logo.webp"
                alt="Joviaa"
                className="h-10 w-auto object-contain mix-blend-screen"
              />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/why-joviaa"
                className="text-background-50/90 hover:text-background-50 text-sm font-label font-medium transition-colors whitespace-nowrap"
              >
                Why Joviaa
              </Link>
              <Link
                to="/shop"
                className="text-background-50/90 hover:text-background-50 text-sm font-label font-medium transition-colors whitespace-nowrap"
              >
                Shop
              </Link>
              <Link
                to="/shop?filter=occasion"
                className="text-background-50/90 hover:text-background-50 text-sm font-label font-medium transition-colors whitespace-nowrap"
              >
                Occasions
              </Link>
              <Link
                to="/durability"
                className="text-background-50/90 hover:text-background-50 text-sm font-label font-medium transition-colors whitespace-nowrap"
              >
                Durability
              </Link>
              <Link
                to="/about"
                className="text-background-50/90 hover:text-background-50 text-sm font-label font-medium transition-colors whitespace-nowrap"
              >
                About
              </Link>
              <Link
                to="/faq"
                className="text-background-50/90 hover:text-background-50 text-sm font-label font-medium transition-colors whitespace-nowrap"
              >
                FAQ
              </Link>
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-4 md:gap-5">
              {/* Search */}
              <Link
                to="/search"
                className="text-background-50/80 hover:text-background-50 transition-colors cursor-pointer"
                aria-label="Search"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-search-line text-lg"></i>
                </div>
              </Link>

              {/* Cart */}
              <button
                onClick={openDrawer}
                className="relative text-background-50/80 hover:text-background-50 transition-colors cursor-pointer"
                aria-label="Cart"
              >
                <div className={`w-5 h-5 flex items-center justify-center ${cartBounce ? 'cart-bounce' : ''}`}>
                  <i className="ri-shopping-bag-line text-lg"></i>
                </div>
                {itemCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-accent-500 text-primary-800 text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">
                    {itemCount}
                  </span>
                )}
              </button>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden text-background-50/80 hover:text-background-50 transition-colors cursor-pointer"
                aria-label="Menu"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
                </div>
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-background-50/10 pt-3">
              <div className="flex flex-col gap-3">
                <Link
                  to="/why-joviaa"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-background-50/80 hover:text-background-50 text-sm font-label font-medium transition-colors py-1"
                >
                  Why Joviaa
                </Link>
                <Link
                  to="/shop"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-background-50/80 hover:text-background-50 text-sm font-label font-medium transition-colors py-1"
                >
                  Shop
                </Link>
                <Link
                  to="/shop?filter=occasion"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-background-50/80 hover:text-background-50 text-sm font-label font-medium transition-colors py-1"
                >
                  Occasions
                </Link>
                <Link
                  to="/durability"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-background-50/80 hover:text-background-50 text-sm font-label font-medium transition-colors py-1"
                >
                  Durability
                </Link>
                <Link
                  to="/about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-background-50/80 hover:text-background-50 text-sm font-label font-medium transition-colors py-1"
                >
                  About
                </Link>
                <Link
                  to="/faq"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-background-50/80 hover:text-background-50 text-sm font-label font-medium transition-colors py-1"
                >
                  FAQ
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>
      <CartDrawer />
    </>
  );
}